import {
    AXNUIContent,
    AXNUILayout,
    MultiLanguageWrapper,
} from "@iddataweb/idw-common-ui/types";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import {
    AXNUI_CONTENT_PROPERTY_TYPE_ID,
    AXNUI_LAYOUT_PROPERTY_TYPE_ID,
    REDIRECT_URLS_PROPERTY_TYPE_ID,
} from "@constants/verification-service-details-constants";
import { AppliedProfileTypes } from "@enums/applied-profile-types";
import { FormattedAppliedProfile } from "@idw-types/data/applied-profile/applied-profile";
import { getProfilePropertyValuesWithProfileIdForPropertyTypeId } from "@utils/applied-profile-util";
import {
    FormattedRpServiceConfiguration,
    FormattedRpServiceConfigurationProperty,
    RpServiceConfigurationProperty,
} from "@idw-types/data/rp-service-configuration";
import { FormattedWorkflowTemplateDetailsTemplate } from "@idw-types/data/workflow-template-details";
import {
    FormattedIdwProfile,
    FormattedIdwProfileProperty,
} from "@idw-types/data/idw-profile";
import { DEFAULT_AXNUI_CONTENT } from "@iddataweb/idw-common-ui/constants";
import {
    getMatchesCustomRedirectUrl,
    getMatchesStaticRedirectUrl,
} from "@iddataweb/idw-common-ui/utils";
import { BaseRedirectUrl, RedirectUrlInformation } from "@idw-types/redirect-url-information";
import { PageLevelTypes } from "@enums/page-level-types";
import { RedirectUrlOriginTypes } from "@enums/redirect-url-origin-types";

export enum RedirectUrlUpdateModeTypes {
    ADD = 'add',
    EDIT = 'edit',
    REMOVE = 'remove'
}

export const getCombinedAppliedProfileRedirectURLs = (
    appliedProfiles: Array<FormattedAppliedProfile>,
    editable: boolean
): Array<BaseRedirectUrl> => {
    const {
        profileRedirectURLsWithoutKey,
        profileRedirectURLsWithKey,
    } = getAppliedProfileRedirectURLs(appliedProfiles, "", editable);

    return [...profileRedirectURLsWithoutKey, ...profileRedirectURLsWithKey];
};

export const getAppliedProfileRedirectURLs = (
    appliedProfiles: Array<FormattedAppliedProfile>,
    profileKey: string,
    isEditable: boolean
): {
    profileRedirectURLsWithoutKey: Array<BaseRedirectUrl>;
    profileRedirectURLsWithKey: Array<BaseRedirectUrl>;
} => {
    // get all of the property values that have been added to applied profiles for redirectURLs
    const profileProperties: Array<{
        profileId: string;
        profileType: AppliedProfileTypes;
        value: { [key: string]: Array<string> };
    }> = getProfilePropertyValuesWithProfileIdForPropertyTypeId(
        appliedProfiles,
        REDIRECT_URLS_PROPERTY_TYPE_ID
    );

    const profileRedirectURLsWithoutKey: Array<BaseRedirectUrl> = [];
    const profileRedirectURLsWithKey: Array<BaseRedirectUrl> = [];
    // for each property - get all of the redirect URLs and put them into an array
    profileProperties.forEach((profileProperty) => {
        const keys = Object.keys(profileProperty.value);
        // if the key is the workflow ID plus a prefix of "w", it should be editable
        keys.forEach((key) => {
            if (profileProperty.value[key] && profileProperty.value[key].length > 0) {
                // for each key - put the redirect urls into the final array
                profileProperty.value[key].forEach((redirectURL, index) => {
                    if (key === profileKey) {
                        profileRedirectURLsWithKey.push({ index, url: redirectURL, isEditable, redirectUrlOrigin: RedirectUrlOriginTypes.PROFILE_REDIRECT_URL });
                    } else {
                        profileRedirectURLsWithoutKey.push({ index, url: redirectURL, isEditable: false, redirectUrlOrigin: RedirectUrlOriginTypes.PROFILE_REDIRECT_URL });
                    }
                });
            }
        });
    });

    return { profileRedirectURLsWithKey, profileRedirectURLsWithoutKey };
};

export const getRpServiceConfigurationPropertyRedirectURLs = (
    properties: Array<RpServiceConfigurationProperty>,
    rpServiceConfigurationId: string,
    editable: boolean
) => {
    const redirectsProperty = properties.find(
        (p) => p.propertyID === REDIRECT_URLS_PROPERTY_TYPE_ID
    );
    if (redirectsProperty?.value) {
        return getRedirectURLsFromJSONString(
            redirectsProperty.value,
            rpServiceConfigurationId,
            editable
        );
    }
    return [];
};

export const getFormattedRpServiceConfigurationPropertyRedirectURLs = (
    properties: Array<FormattedRpServiceConfigurationProperty>,
    rpServiceConfigurationId: string,
    editable: boolean
) => {
    const redirectsProperty = properties.find(
        (p) => p.propertyTypeId === REDIRECT_URLS_PROPERTY_TYPE_ID
    );
    if (
        redirectsProperty &&
        redirectsProperty.addedToService &&
        redirectsProperty.value
    ) {
        return getRedirectURLsFromJSONString(
            redirectsProperty.value,
            rpServiceConfigurationId,
            editable
        );
    }
    return [];
};

export const getRedirectURLsFromJSONString = (
    redirectsJSONString: any,
    rpServiceID: string,
    isRedirectUrlEditable?: boolean
): Array<BaseRedirectUrl> => {
    const redirects: Array<BaseRedirectUrl> = [];
    try {
        const redirectsJSON = JSON.parse(redirectsJSONString);
        const keys = Object.keys(redirectsJSON);
        // if the key is the workflow ID plus a prefix of "w", it should be editable
        keys.forEach((key) => {
            if (redirectsJSON[key] && redirectsJSON[key].length > 0) {
                redirectsJSON[key].forEach((url: string, index: number) => {
                    redirects.push({
                        index,
                        url,
                        rpServiceID,
                        isEditable: isRedirectUrlEditable,
                        redirectUrlOrigin: RedirectUrlOriginTypes.PROPERTY_REDIRECT_URL
                    });
                });
            }
        });
    } catch (error) {
        console.log(`error parsing redirectsJSON`, error);
    }

    return redirects;
};


export const extractRedirectURLsFromWorkflowTemplatePartial = (
    template: Partial<FormattedWorkflowTemplateDetailsTemplate>
): Array<BaseRedirectUrl> => {
    const finalRedirectURLs: Array<BaseRedirectUrl> = [];

    if (template.appliedProfiles && template.appliedProfiles.length > 0) {
        const { profileRedirectURLsWithKey } = getAppliedProfileRedirectURLs(
            template.appliedProfiles,
            "",
            false
        );

        finalRedirectURLs.push(...profileRedirectURLsWithKey);
    }

    if ((template?.workflow?.rpServiceWorkflows || []).length > 0) {
        const primaryService = template.workflow.rpServiceWorkflows.find(
            (rpsw) => rpsw.primaryServiceFlag
        );
        if (primaryService) {
            const rpservice = (template.rpServices || []).find(
                (service) => service.id === `${primaryService.rpAXNServiceID}`
            );
            if (rpservice) {
                const propertyUrls = getFormattedRpServiceConfigurationPropertyRedirectURLs(
                    rpservice.properties,
                    rpservice.id,
                    null
                );
                propertyUrls.forEach((propertyUrl) => {
                    finalRedirectURLs.push(propertyUrl);
                });
                (rpservice.redirects || []).forEach((redirect, index) => {
                    finalRedirectURLs.push({ index, url: redirect.redirectURI, isEditable: false, redirectUrlOrigin: RedirectUrlOriginTypes.REDIRECT });
                });
            }
        }
    }

    return finalRedirectURLs;
};

const handleRedirectUrlsProperty = (
    profileProperty: FormattedIdwProfileProperty,
    workflowProfileKey: string,
    index: number,
    newRedirectUrl: string
) => {
    if (profileProperty.addedToProfile && profileProperty.value) {
        try {
            const existingValue: { [key: string]: Array<string> } = JSON.parse(
                profileProperty.value
            );
            if (existingValue[workflowProfileKey]) {
                const newRedirects = [...existingValue[workflowProfileKey]];
                newRedirects[index] = newRedirectUrl;
                existingValue[workflowProfileKey] = newRedirects;
            }
            return {
                ...profileProperty,
                value: JSON.stringify(existingValue),
            };
        } catch (error) {
            console.error(error);
        }
    }

    return null;
};

const handleLayoutProperty = (
    profileProperty: FormattedIdwProfileProperty,
    newRedirectUrl: string,
    redirectUrl: string
) => {
    if (profileProperty.addedToProfile && profileProperty.value) {
        try {
            const existingValue: AXNUILayout = JSON.parse(profileProperty.value);
            //layout section exists for previous redirect URL - move it to new one
            if (existingValue.mockRP?.[redirectUrl]) {
                const newValue: AXNUILayout = {
                    ...existingValue,
                    mockRP: {
                        ...existingValue.mockRP,
                        [newRedirectUrl]: existingValue.mockRP[redirectUrl],
                        [redirectUrl]: undefined,
                    },
                };
                return {
                    ...profileProperty,
                    value: JSON.stringify(newValue),
                };
            }
        } catch (error) {
            console.error(error);
        }
    }

    return null;
};

const handleContentProperty = (
    profileProperty: FormattedIdwProfileProperty,
    newRedirectUrl: string,
    redirectUrl: string,
    isMultiLanguageEnabled: boolean,
    availableLanguages: Array<LocaleTypes>
) => {
    if (profileProperty.addedToProfile && profileProperty.value) {
        try {
            //if we have a multi-lang property, we need to update the redirect url key in every
            //language it exists
            if (isMultiLanguageEnabled && availableLanguages.length > 0) {
                const newMultiLanguageAXNUIContent: {
                    [key in LocaleTypes]?: AXNUIContent;
                } = {};
                const existingValue: MultiLanguageWrapper<AXNUIContent> = JSON.parse(
                    profileProperty.value
                );
                //so we iterate over each language
                availableLanguages.forEach((al) => {
                    if (existingValue[al]) {
                        //and if the redirect url key exists, we change it to the new redirect url key
                        if (existingValue[al].mockRP?.[redirectUrl]) {
                            newMultiLanguageAXNUIContent[al] = {
                                ...existingValue[al],
                                mockRP: {
                                    ...existingValue[al].mockRP,
                                    [newRedirectUrl]: existingValue[al].mockRP[redirectUrl],
                                    [redirectUrl]: undefined,
                                },
                            };
                        } else {
                            //if it doesnt exist, we leave the content alone
                            newMultiLanguageAXNUIContent[al] = existingValue[al];
                        }
                    } else {
                        //if the content for that language doesn't exist at all, we set it to the default
                        newMultiLanguageAXNUIContent[al] = DEFAULT_AXNUI_CONTENT[al];
                    }
                });
                return {
                    ...profileProperty,
                    value: JSON.stringify(newMultiLanguageAXNUIContent),
                };
            } else {
                //here we have a single language content object
                const existingAXNUIContent: AXNUIContent = JSON.parse(
                    profileProperty.value
                );
                //if the redirect url key exists, we replace it with the new one
                if (existingAXNUIContent.mockRP?.[redirectUrl]) {
                    const newValue: AXNUIContent = {
                        ...existingAXNUIContent,
                        mockRP: {
                            ...existingAXNUIContent.mockRP,
                            [newRedirectUrl]: existingAXNUIContent.mockRP[redirectUrl],
                            [redirectUrl]: undefined,
                        },
                    };
                    return {
                        ...profileProperty,
                        value: JSON.stringify(newValue),
                    };
                }
            }
        } catch (error) {
            console.error(error);
        }
    }

    return null;
};

export const handleWorkflowRedirectChangeAtIndex = (
    workflowProfile: FormattedIdwProfile,
    workflowProfileKey: string,
    index: number,
    redirectUrlOrigin: RedirectUrlOriginTypes,
    newRedirectUrl: string,
    redirectUrl: string,
    primaryRpService: FormattedRpServiceConfiguration,
    rpServiceConfigurations: Array<FormattedRpServiceConfiguration>,
    isMultiLanguageEnabled: boolean,
    availableLanguages: Array<LocaleTypes>,
    setWorkflowProfile: (profile: FormattedIdwProfile) => void,
    setRpServiceConfigurationsState: (rpServiceConfigurations: Array<FormattedRpServiceConfiguration>) => void
) => {
    if (redirectUrlOrigin === RedirectUrlOriginTypes.CALLBACK_URL) {
        const newConfigs = rpServiceConfigurations.map(config => {
            if (config.id === primaryRpService.id) {
                return {
                    ...config,
                    callBackURL: newRedirectUrl
                };
            } else {
                return config;
            }
        });
        setRpServiceConfigurationsState(newConfigs);
    } else {
        const newProperties: Array<FormattedIdwProfileProperty> = workflowProfile.profileProperties.map(
            (profileProperty) => {
                if (
                    profileProperty.availableProperty?.id === REDIRECT_URLS_PROPERTY_TYPE_ID
                ) {
                    const newRedirectUrlsProperty = handleRedirectUrlsProperty(
                        profileProperty,
                        workflowProfileKey,
                        index,
                        newRedirectUrl
                    );
                    if (newRedirectUrlsProperty) {
                        return newRedirectUrlsProperty;
                    }
                } else if (
                    profileProperty.availableProperty?.id === AXNUI_LAYOUT_PROPERTY_TYPE_ID
                ) {
                    const newLayoutProperty = handleLayoutProperty(
                        profileProperty,
                        newRedirectUrl,
                        redirectUrl
                    );
                    if (newLayoutProperty) {
                        return newLayoutProperty;
                    }
                } else if (
                    profileProperty.availableProperty?.id === AXNUI_CONTENT_PROPERTY_TYPE_ID
                ) {
                    const newContentProperty = handleContentProperty(
                        profileProperty,
                        newRedirectUrl,
                        redirectUrl,
                        isMultiLanguageEnabled,
                        availableLanguages
                    );
                    if (newContentProperty) {
                        return newContentProperty;
                    }
                }
    
                return profileProperty;
            }
        );
    
        setWorkflowProfile({
            ...workflowProfile,
            profileProperties: newProperties,
        });
    }
};

export const handleRemoveWorkflowRedirect = (
    workflowProfile: FormattedIdwProfile,
    workflowProfileKey: string,
    index: number,
    redirectUrlOrigin: RedirectUrlOriginTypes,
    redirectUrlsInformation: RedirectUrlInformation,
    primaryRpService: FormattedRpServiceConfiguration,
    rpServiceConfigurations: Array<FormattedRpServiceConfiguration>,
    setWorkflowProfile: (workflowProfile: FormattedIdwProfile) => void,
    setRpServiceConfigurationsState: (rpServiecConfigurations: Array<FormattedRpServiceConfiguration>) => void
) => {
    if (redirectUrlOrigin === RedirectUrlOriginTypes.CALLBACK_URL) {
        let newCallbackURL: BaseRedirectUrl;

        if (redirectUrlsInformation.customRedirectUrlsWithOriginalIndex.length > 0) {
            newCallbackURL = redirectUrlsInformation.customRedirectUrlsWithOriginalIndex.find(url => url.redirectUrlOrigin !== RedirectUrlOriginTypes.CALLBACK_URL);
        }
        if (!newCallbackURL && redirectUrlsInformation.staticRedirectUrlsWithOriginalIndex.length > 0) {
            newCallbackURL = redirectUrlsInformation.staticRedirectUrlsWithOriginalIndex.find(url => url.redirectUrlOrigin !== RedirectUrlOriginTypes.CALLBACK_URL);
        }
        if (!newCallbackURL && redirectUrlsInformation.customerRedirectUrlsWithOriginalIndex.length > 0) {
            newCallbackURL = redirectUrlsInformation.customerRedirectUrlsWithOriginalIndex.find(url => url.redirectUrlOrigin !== RedirectUrlOriginTypes.CALLBACK_URL);
        }

        const newConfigs = rpServiceConfigurations.map(config => {
            if (config.id === primaryRpService.id) {
                return {
                    ...config,
                    callBackURL: newCallbackURL?.url || null
                };
            } else {
                return config;
            }
        });


        if (newCallbackURL) {
            const newProperties = updateRedirectUrls(
                workflowProfile.profileProperties,
                workflowProfileKey,
                RedirectUrlUpdateModeTypes.REMOVE,
                PageLevelTypes.WORKFLOW,
                null,
                newCallbackURL.index
            );
        
            setWorkflowProfile({
                ...workflowProfile,
                profileProperties: newProperties as Array<FormattedIdwProfileProperty>,
            });

            setRpServiceConfigurationsState(newConfigs);
        } else {
            setRpServiceConfigurationsState(newConfigs);
        }
    } else {
        const newProperties = updateRedirectUrls(
            workflowProfile.profileProperties,
            workflowProfileKey,
            RedirectUrlUpdateModeTypes.REMOVE,
            PageLevelTypes.WORKFLOW,
            null,
            index
        );

        setWorkflowProfile({
            ...workflowProfile,
            profileProperties: newProperties as Array<FormattedIdwProfileProperty>,
        });
    }
};

export const handleAddWorkflowRedirect = (
    workflowProfile: FormattedIdwProfile,
    workflowProfileKey: string,
    newRedirectUrl: string,
    primaryRpService: FormattedRpServiceConfiguration,
    rpServiceConfigurations: Array<FormattedRpServiceConfiguration>,
    setWorkflowProfile: (workflowProfile: FormattedIdwProfile) => void,
    setRpServiceConfigurationsState: (rpServiceConfigurations: Array<FormattedRpServiceConfiguration>) => void
) => {
    if (!primaryRpService.callBackURL) {
        const newConfigs = rpServiceConfigurations.map(config => {
            if (config.id === primaryRpService.id) {
                return {
                    ...config,
                    callBackURL: newRedirectUrl
                };
            } else {
                return config;
            }
        });
        setRpServiceConfigurationsState(newConfigs);
    } else {
        const newProperties = updateRedirectUrls(
            workflowProfile.profileProperties,
            workflowProfileKey,
            RedirectUrlUpdateModeTypes.ADD,
            PageLevelTypes.WORKFLOW,
            newRedirectUrl
        );
    
        setWorkflowProfile({
            ...workflowProfile,
            profileProperties: newProperties as Array<FormattedIdwProfileProperty>
        });
    }
};

export const setBaseRedirectUrl = (redirectUrl: BaseRedirectUrl) => {
    return redirectUrl;
}

export const getRedirectUrlsInformation = (
    redirectUrls: Array<BaseRedirectUrl>,
    shouldShowPreviewComponent: boolean
): RedirectUrlInformation => {

    const initialRedirectUrlInformation: RedirectUrlInformation = {
        customRedirectUrlsWithOriginalIndex: [],
        staticRedirectUrlsWithOriginalIndex: [],
        customerRedirectUrlsWithOriginalIndex: [],
        previewableRedirectUrls: [],
        redirectUrls
    };

    const results: RedirectUrlInformation = redirectUrls.reduce((finalRedirectUrls, redirectUrl) => {
        const { url, isEditable, redirectUrlOrigin, index } = redirectUrl;
        const urlWithIndex = { url, index, isEditable, redirectUrlOrigin };
        const isCustomRedirectUrl = getMatchesCustomRedirectUrl(urlWithIndex.url);
        const isNonDisplayRedirectUrls = !isCustomRedirectUrl;

        if (isCustomRedirectUrl) {
            finalRedirectUrls.customRedirectUrlsWithOriginalIndex.push(urlWithIndex);

            if (shouldShowPreviewComponent) {
                finalRedirectUrls.previewableRedirectUrls.push(urlWithIndex.url);
            }
        } else {
            const isStaticRedirectUrl = getMatchesStaticRedirectUrl(urlWithIndex.url);
            if (isStaticRedirectUrl) {
                finalRedirectUrls.staticRedirectUrlsWithOriginalIndex.push(urlWithIndex);

                if (shouldShowPreviewComponent) {
                    finalRedirectUrls.previewableRedirectUrls.push(urlWithIndex.url);
                }
            } else {
                if (isNonDisplayRedirectUrls) {
                    finalRedirectUrls.customerRedirectUrlsWithOriginalIndex.push(urlWithIndex);
                }
            }
        }

        return finalRedirectUrls;

    }, initialRedirectUrlInformation);

    return results;
};

export const isFormattedRpServiceConfigurationProperty = (input: FormattedRpServiceConfigurationProperty | FormattedIdwProfileProperty): input is FormattedRpServiceConfigurationProperty => {
    return Object.keys(input).includes('addedToService');
}

const isRedirectUrlProperty = (property: FormattedRpServiceConfigurationProperty | FormattedIdwProfileProperty, pageLevel: PageLevelTypes): { exist: boolean; addedToPropertyName: ('addedToService' | 'addedToProfile' | null) } => {
    if (pageLevel === PageLevelTypes.APIKEY) {
        return isServiceRedirectProperty(property as FormattedRpServiceConfigurationProperty);
    } else if (pageLevel === PageLevelTypes.WORKFLOW) {
        return isWorkflowRedirectProperty(property as FormattedIdwProfileProperty);
    }

    return { exist: false, addedToPropertyName: null };

}

const isServiceRedirectProperty = (property: FormattedRpServiceConfigurationProperty): { exist: boolean; addedToPropertyName: 'addedToService' } => {
    return {
        exist: property.propertyTypeId === REDIRECT_URLS_PROPERTY_TYPE_ID,
        addedToPropertyName: 'addedToService'
    };
}

const isWorkflowRedirectProperty = (profileProperty: FormattedIdwProfileProperty): { exist: boolean; addedToPropertyName: 'addedToProfile'; } => {
    return {
        exist: profileProperty.availableProperty?.id === REDIRECT_URLS_PROPERTY_TYPE_ID,
        addedToPropertyName: 'addedToProfile'

    };
}

const updateRedirectUrlItem = (
    redirectUrlKey: string,
    existingValue: Record<string, Array<string>>,
    mode: RedirectUrlUpdateModeTypes,
    newRedirectUrl: string,
    index?: number
) => {
    if (existingValue[redirectUrlKey]) {
        if (mode === RedirectUrlUpdateModeTypes.EDIT) {
            const newRedirects = [...existingValue[redirectUrlKey]];
            newRedirects[index] = newRedirectUrl;
            existingValue[redirectUrlKey] = newRedirects;
        } else if (mode === RedirectUrlUpdateModeTypes.ADD) {
            existingValue[redirectUrlKey].push(newRedirectUrl);
        } else if (mode === RedirectUrlUpdateModeTypes.REMOVE) {
            existingValue[redirectUrlKey].splice(index, 1);
        }
    } else {
        if (mode === RedirectUrlUpdateModeTypes.ADD) {
            existingValue[redirectUrlKey] = [newRedirectUrl];
        }
    }

    return existingValue;
}

const addRedirectPropertyOnValueNotSet = <T>(property: T, addedToPropertyName: string, redirectUrlKey: string, newRedirectUrl: string): T => {
    return {
        ...property,
        [addedToPropertyName]: true,
        value: JSON.stringify({ [redirectUrlKey]: [newRedirectUrl] })
    } as T;
}

export const getRedirectURLsPropertyValueJSON = (
    property: FormattedRpServiceConfigurationProperty | FormattedIdwProfileProperty
): Record<string, string[]> => {
    const isRpServiceProperty = isFormattedRpServiceConfigurationProperty(property);
    const isAdded = isRpServiceProperty ? property['addedToService'] : property['addedToProfile'];
    if (isAdded && property.value !== null) {
        try {
            const existingValue: Record<string, Array<string>> = JSON.parse(property.value);
            return existingValue;
        } catch (error) {
            console.error(error);
        }
    }

    return null;
}

export const updateRedirectUrls = <T extends FormattedRpServiceConfigurationProperty | FormattedIdwProfileProperty>(
    properties: Array<T>,
    redirectUrlKey: string,
    mode: RedirectUrlUpdateModeTypes,
    pageLevel: PageLevelTypes,
    newRedirectUrl: string,
    index?: number): Array<T> => {

    return properties.map(property => {
        const { exist, addedToPropertyName } = isRedirectUrlProperty(property, pageLevel);

        if (exist && addedToPropertyName !== null) {
            const redirectURLPropertyValueJSON = getRedirectURLsPropertyValueJSON(property);

            if (redirectURLPropertyValueJSON) {
                return {
                    ...property,
                    value: JSON.stringify(
                        updateRedirectUrlItem(
                            redirectUrlKey,
                            redirectURLPropertyValueJSON,
                            mode,
                            newRedirectUrl,
                            index
                        ))
                };
            } else if (mode === RedirectUrlUpdateModeTypes.ADD) {
                return addRedirectPropertyOnValueNotSet(property, addedToPropertyName, redirectUrlKey, newRedirectUrl);
            }
        }

        return property;
    });
}
